import React, { useEffect, useState } from 'react';
import {
    Row, Col, Collapse, Switch, Slider, InputNumber,
} from 'antd';

import { YoloAugmentationConfig } from 'proto/nn_training/DetectorTraining_pb';

import type { FormValues } from '../export-dataset-modal';

const { Panel } = Collapse;

interface FlipProps {
    initialValues: FormValues;
    augmintationValues?: YoloAugmentationConfig.AsObject;
}

const Flip = (props: FlipProps): JSX.Element => {
    const { initialValues, augmintationValues } = props;

    const [fliplrValue, setFliplrValue] = useState<number>(0);
    const [flipudValue, setFlipudValue] = useState<number>(0);
    const [checkedFlip, setCheckedFlip] = useState<boolean>(false);

    // Коррекция ввода значения в Input (Оставляем только цифры)
    const inputParser = (value: string | undefined): number => {
        const result = Number(value?.replace(/[^0-9.,]/g, '').replace(/[,]/g, '.')).toFixed(2);

        if (Number(result) === 0) {
            return 0;
        }

        if (Number(result) > 100) {
            return 100;
        }

        return Number(result);
    };

    // Конвертация значений аугментации по умолчанию в значения слайдера
    const getValueToSlider = (value?: number): number => (
        Math.floor((Number(value) * 100) * 100) / 100
    );

    // Заносим значения по умолчанию, полученные с gRPC
    useEffect(() => {
        setFliplrValue(getValueToSlider(augmintationValues?.fliplr));
        setFlipudValue(getValueToSlider(augmintationValues?.flipud));
    }, [augmintationValues]);

    // Если значения не указаны/указаны, выключаем/включаем Switch
    useEffect(() => {
        if (fliplrValue > 0 || flipudValue > 0) {
            setCheckedFlip(true);
        } else {
            setCheckedFlip(false);
        }

        // Добавляем в initialValues информацию по аугментации для создания Датасета
        if (initialValues.exportDatasetParams.augmentation) {
            // Если значения аугментации по умолчанию изменились, указываем значение из слайдера
            if (getValueToSlider(augmintationValues?.fliplr) !== fliplrValue) {
                initialValues.exportDatasetParams.augmentation.fliplr = fliplrValue / 100;
            }

            if (getValueToSlider(augmintationValues?.flipud) !== flipudValue) {
                initialValues.exportDatasetParams.augmentation.flipud = flipudValue / 100;
            }
        }
    }, [fliplrValue, flipudValue]);

    // Если выключаем Switch, значения сбрасываются до 0
    useEffect(() => {
        if (!checkedFlip) {
            setFliplrValue(0);
            setFlipudValue(0);
        }
    }, [checkedFlip]);

    return (
        <Col span={24}>
            <Collapse style={{ borderRadius: 6 }}>
                <Panel
                    style={{ alignItems: 'center', borderRadius: 6 }}
                    header={<div>Отражение</div>}
                    extra={(
                        <div onClick={(event) => event.stopPropagation()} aria-hidden='true'>
                            <Switch
                                checked={checkedFlip}
                                onChange={(value) => setCheckedFlip(value)}
                            />
                        </div>
                    )}
                    showArrow={false}
                    key='Augment_4'
                >
                    <div>
                        Добавьте горизонтальное или вертикальное отражение, чтобы ваша
                        модель была нечувствительна к ориентации объекта.
                    </div>
                    <Row gutter={[32, 12]} style={{ marginTop: 25 }}>
                        <Col span={24}>
                            <Row gutter={[32, 12]}>
                                <Col span={6}>
                                    <div
                                        style={{
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontWeight: 600,
                                        }}
                                    >
                                        По горизонтали
                                    </div>
                                </Col>
                                <Col span={14}>
                                    <Slider
                                        marks={{ 0: '0%', 100: '100%' }}
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        value={fliplrValue}
                                        onChange={(value) => setFliplrValue(value)}
                                        tooltip={{
                                            formatter: (value?: number): string | undefined => `${value}%`,
                                        }}
                                        trackStyle={{ background: '#368585', height: '5px' }}
                                        railStyle={{
                                            background: '#CECECE',
                                            borderRadius: '5px',
                                        }}
                                        handleStyle={{
                                            background: '#ffffff',
                                            border: 'solid 2px #4d6674',
                                            width: '18px',
                                            height: '18px',
                                            marginTop: '-7px',
                                        }}
                                    />
                                </Col>
                                <Col span={4}>
                                    <InputNumber
                                        value={fliplrValue}
                                        onChange={(value) => (
                                            value ? setFliplrValue(value) : setFliplrValue(0)
                                        )}
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        controls={false}
                                        addonAfter='%'
                                        parser={(value): number => inputParser(value)}
                                    />
                                </Col>
                            </Row>
                        </Col>

                        <Col span={24}>
                            <Row gutter={[32, 12]}>
                                <Col span={6}>
                                    <div
                                        style={{
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontWeight: 600,
                                        }}
                                    >
                                        По вертикали
                                    </div>
                                </Col>
                                <Col span={14}>
                                    <Slider
                                        marks={{ 0: '0%', 100: '100%' }}
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        value={flipudValue}
                                        onChange={(value) => setFlipudValue(value)}
                                        tooltip={{
                                            formatter: (value?: number): string => `${value}%`,
                                        }}
                                        trackStyle={{ background: '#368585', height: '5px' }}
                                        railStyle={{
                                            background: '#CECECE',
                                            borderRadius: '5px',
                                        }}
                                        handleStyle={{
                                            background: '#ffffff',
                                            border: 'solid 2px #4d6674',
                                            width: '18px',
                                            height: '18px',
                                            marginTop: '-7px',
                                        }}
                                    />
                                </Col>
                                <Col span={4}>
                                    <InputNumber
                                        value={flipudValue}
                                        onChange={(value) => (
                                            value ? setFlipudValue(value) : setFlipudValue(0)
                                        )}
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        controls={false}
                                        addonAfter='%'
                                        parser={(value): number => inputParser(value)}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Panel>
            </Collapse>
        </Col>
    );
};

export default Flip;
